import React, { useState, useEffect } from 'react';
import {
    AiOutlinePlus,
    AiOutlineSearch,
    AiOutlineEdit,
    AiOutlineDelete,
    AiOutlineDownload,
    AiOutlinePrinter,
    AiOutlineMail,
    AiOutlineSend,
    AiOutlineInbox,
    AiOutlineClockCircle,
    AiOutlineCheckCircle,
    AiOutlineExclamationCircle,
    AiOutlineUser,
    AiOutlineCalendar,
    AiOutlineEye,
    AiOutlineUndo,
    AiOutlineForward,
} from 'react-icons/ai';
import styles from './BusinessEmail.module.css';

function BusinessEmail() {
    const [emails, setEmails] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('all');
    const [showComposeModal, setShowComposeModal] = useState(false);
    const [selectedEmail, setSelectedEmail] = useState(null);
    const [showReplyModal, setShowReplyModal] = useState(false);

    // Sample data - replace with actual API calls
    const sampleEmails = [
        {
            id: 1,
            from: 'customer@example.com',
            to: 'business@company.com',
            subject: 'Product Inquiry - iPhone 15 Pro',
            body: 'Hi, I am interested in purchasing the iPhone 15 Pro. Could you please provide more details about availability and pricing?',
            date: '2024-01-15T10:30:00Z',
            status: 'unread',
            priority: 'high',
            category: 'inquiry',
        },
        {
            id: 2,
            from: 'supplier@tech.com',
            to: 'business@company.com',
            subject: 'Order Confirmation - Tech Supplies',
            body: 'Your order #12345 has been confirmed and will be shipped within 2-3 business days.',
            date: '2024-01-14T14:20:00Z',
            status: 'read',
            priority: 'normal',
            category: 'order',
        },
        {
            id: 3,
            from: 'support@payment.com',
            to: 'business@company.com',
            subject: 'Payment Gateway Update',
            body: 'We are updating our payment gateway system. Please review the attached documentation for the new integration process.',
            date: '2024-01-13T09:15:00Z',
            status: 'read',
            priority: 'low',
            category: 'system',
        },
        {
            id: 4,
            from: 'marketing@partner.com',
            to: 'business@company.com',
            subject: 'Partnership Proposal',
            body: 'We would like to discuss a potential partnership opportunity that could benefit both our companies.',
            date: '2024-01-12T16:45:00Z',
            status: 'unread',
            priority: 'high',
            category: 'partnership',
        },
    ];

    useEffect(() => {
        setEmails(sampleEmails);
    }, []);

    const formatCurrency = (amount) => {
        return `৳${amount.toLocaleString('en-US', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        })}`;
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });
    };

    const formatTime = (dateString) => {
        return new Date(dateString).toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    const getPriorityBadge = (priority) => {
        const priorityClasses = {
            high: styles.priorityHigh,
            normal: styles.priorityNormal,
            low: styles.priorityLow,
        };
        return `${styles.priorityBadge} ${
            priorityClasses[priority] || styles.priorityNormal
        }`;
    };

    const getStatusBadge = (status) => {
        const statusClasses = {
            unread: styles.statusUnread,
            read: styles.statusRead,
            replied: styles.statusReplied,
        };
        return `${styles.statusBadge} ${
            statusClasses[status] || styles.statusRead
        }`;
    };

    const filteredEmails = emails.filter((email) => {
        const matchesSearch =
            email.from.toLowerCase().includes(searchTerm.toLowerCase()) ||
            email.to.toLowerCase().includes(searchTerm.toLowerCase()) ||
            email.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
            email.body.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesStatus =
            filterStatus === 'all' || email.status === filterStatus;
        return matchesSearch && matchesStatus;
    });

    const getTotalEmails = () => emails.length;
    const getUnreadEmails = () =>
        emails.filter((email) => email.status === 'unread').length;
    const getTodayEmails = () => {
        const today = new Date().toDateString();
        return emails.filter(
            (email) => new Date(email.date).toDateString() === today
        ).length;
    };
    const getHighPriorityEmails = () =>
        emails.filter((email) => email.priority === 'high').length;

    return (
        <div className={styles.emailContainer}>
            {/* Header Section */}
            <div className={styles.header}>
                <div className={styles.headerLeft}>
                    <h1 className={styles.pageTitle}>
                        Business Email Management
                    </h1>
                    <p className={styles.pageSubtitle}>
                        Manage your business communications and customer
                        inquiries
                    </p>
                </div>
                <div className={styles.headerRight}>
                    <button
                        className={styles.primaryButton}
                        onClick={() => setShowComposeModal(true)}
                    >
                        <AiOutlinePlus className={styles.buttonIcon} />
                        Compose Email
                    </button>
                </div>
            </div>

            {/* Stats Cards */}
            <div className={styles.statsGrid}>
                <div className={styles.statCard}>
                    <div className={styles.statIcon}>
                        <AiOutlineInbox />
                    </div>
                    <div className={styles.statContent}>
                        <div className={styles.statValue}>
                            {getTotalEmails()}
                        </div>
                        <div className={styles.statLabel}>Total Emails</div>
                    </div>
                </div>
                <div className={styles.statCard}>
                    <div className={styles.statIcon}>
                        <AiOutlineMail />
                    </div>
                    <div className={styles.statContent}>
                        <div className={styles.statValue}>
                            {getUnreadEmails()}
                        </div>
                        <div className={styles.statLabel}>Unread</div>
                    </div>
                </div>
                <div className={styles.statCard}>
                    <div className={styles.statIcon}>
                        <AiOutlineCalendar />
                    </div>
                    <div className={styles.statContent}>
                        <div className={styles.statValue}>
                            {getTodayEmails()}
                        </div>
                        <div className={styles.statLabel}>Today</div>
                    </div>
                </div>
                <div className={styles.statCard}>
                    <div className={styles.statIcon}>
                        <AiOutlineExclamationCircle />
                    </div>
                    <div className={styles.statContent}>
                        <div className={styles.statValue}>
                            {getHighPriorityEmails()}
                        </div>
                        <div className={styles.statLabel}>High Priority</div>
                    </div>
                </div>
            </div>

            {/* Search and Filters */}
            <div className={styles.filtersSection}>
                <div className={styles.searchBox}>
                    <input
                        type="text"
                        placeholder="Search emails..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className={styles.searchInput}
                    />
                    <AiOutlineSearch className={styles.searchIcon} />
                </div>
                <div className={styles.filterButtons}>
                    <button
                        className={`${styles.filterButton} ${
                            filterStatus === 'all' ? styles.filterActive : ''
                        }`}
                        onClick={() => setFilterStatus('all')}
                    >
                        All
                    </button>
                    <button
                        className={`${styles.filterButton} ${
                            filterStatus === 'unread' ? styles.filterActive : ''
                        }`}
                        onClick={() => setFilterStatus('unread')}
                    >
                        Unread
                    </button>
                    <button
                        className={`${styles.filterButton} ${
                            filterStatus === 'read' ? styles.filterActive : ''
                        }`}
                        onClick={() => setFilterStatus('read')}
                    >
                        Read
                    </button>
                    <button className={styles.actionButton}>
                        <AiOutlineDownload className={styles.actionIcon} />
                        Export
                    </button>
                    <button className={styles.actionButton}>
                        <AiOutlinePrinter className={styles.actionIcon} />
                        Print
                    </button>
                </div>
            </div>

            {/* Email List */}
            <div className={styles.emailList}>
                <div className={styles.emailListHeader}>
                    <div className={styles.listTitle}>Recent Emails</div>
                    <div className={styles.listActions}>
                        <button
                            className={styles.composeButton}
                            onClick={() => setShowComposeModal(true)}
                        >
                            <AiOutlinePlus className={styles.actionIcon} />
                            Compose
                        </button>
                    </div>
                </div>

                <div className={styles.emailListContent}>
                    {filteredEmails.length === 0 ? (
                        <div className={styles.emptyState}>
                            <div className={styles.emptyIcon}>
                                <AiOutlineMail />
                            </div>
                            <h3 className={styles.emptyTitle}>
                                No emails found
                            </h3>
                            <p className={styles.emptyDescription}>
                                {searchTerm
                                    ? 'Try adjusting your search criteria'
                                    : 'Start composing your first email'}
                            </p>
                            {!searchTerm && (
                                <button
                                    className={styles.emptyActionButton}
                                    onClick={() => setShowComposeModal(true)}
                                >
                                    Compose Email
                                </button>
                            )}
                        </div>
                    ) : (
                        filteredEmails.map((email) => (
                            <div
                                key={email.id}
                                className={`${styles.emailItem} ${
                                    email.status === 'unread'
                                        ? styles.emailUnread
                                        : ''
                                }`}
                                onClick={() => setSelectedEmail(email)}
                            >
                                <div className={styles.emailCheckbox}>
                                    <input type="checkbox" />
                                </div>
                                <div className={styles.emailStatus}>
                                    <span
                                        className={getStatusBadge(email.status)}
                                    >
                                        {email.status}
                                    </span>
                                </div>
                                <div className={styles.emailPriority}>
                                    <span
                                        className={getPriorityBadge(
                                            email.priority
                                        )}
                                    >
                                        {email.priority}
                                    </span>
                                </div>
                                <div className={styles.emailFrom}>
                                    <div className={styles.fromName}>
                                        {email.from}
                                    </div>
                                </div>
                                <div className={styles.emailSubject}>
                                    <div className={styles.subjectText}>
                                        {email.subject}
                                    </div>
                                    <div className={styles.emailPreview}>
                                        {email.body.substring(0, 100)}...
                                    </div>
                                </div>
                                <div className={styles.emailDate}>
                                    <div className={styles.dateValue}>
                                        {formatDate(email.date)}
                                    </div>
                                    <div className={styles.timeValue}>
                                        {formatTime(email.date)}
                                    </div>
                                </div>
                                <div className={styles.emailActions}>
                                    <button
                                        className={styles.actionBtn}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setSelectedEmail(email);
                                        }}
                                        title="View Email"
                                    >
                                        <AiOutlineEye />
                                    </button>
                                    <button
                                        className={styles.actionBtn}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setShowReplyModal(true);
                                        }}
                                        title="Reply"
                                    >
                                        <AiOutlineUndo />
                                    </button>
                                    <button
                                        className={styles.actionBtn}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                        }}
                                        title="Forward"
                                    >
                                        <AiOutlineForward />
                                    </button>
                                    <button
                                        className={styles.actionBtn}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                        }}
                                        title="Delete"
                                    >
                                        <AiOutlineDelete />
                                    </button>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>

            {/* Email Detail Modal */}
            {selectedEmail && (
                <div className={styles.emailModal}>
                    <div className={styles.modalContainer}>
                        <div className={styles.modalHeader}>
                            <h3 className={styles.modalTitle}>
                                {selectedEmail.subject}
                            </h3>
                            <button
                                className={styles.closeButton}
                                onClick={() => setSelectedEmail(null)}
                            >
                                ×
                            </button>
                        </div>
                        <div className={styles.modalContent}>
                            <div className={styles.emailHeader}>
                                <div className={styles.emailMeta}>
                                    <div className={styles.metaRow}>
                                        <span className={styles.metaLabel}>
                                            From:
                                        </span>
                                        <span className={styles.metaValue}>
                                            {selectedEmail.from}
                                        </span>
                                    </div>
                                    <div className={styles.metaRow}>
                                        <span className={styles.metaLabel}>
                                            To:
                                        </span>
                                        <span className={styles.metaValue}>
                                            {selectedEmail.to}
                                        </span>
                                    </div>
                                    <div className={styles.metaRow}>
                                        <span className={styles.metaLabel}>
                                            Date:
                                        </span>
                                        <span className={styles.metaValue}>
                                            {formatDate(selectedEmail.date)} at{' '}
                                            {formatTime(selectedEmail.date)}
                                        </span>
                                    </div>
                                </div>
                                <div className={styles.emailBadges}>
                                    <span
                                        className={getPriorityBadge(
                                            selectedEmail.priority
                                        )}
                                    >
                                        {selectedEmail.priority}
                                    </span>
                                    <span
                                        className={getStatusBadge(
                                            selectedEmail.status
                                        )}
                                    >
                                        {selectedEmail.status}
                                    </span>
                                </div>
                            </div>
                            <div className={styles.emailBody}>
                                <div className={styles.bodyContent}>
                                    {selectedEmail.body}
                                </div>
                            </div>
                            <div className={styles.emailActions}>
                                <button className={styles.replyButton}>
                                    <AiOutlineUndo
                                        className={styles.buttonIcon}
                                    />
                                    Reply
                                </button>
                                <button className={styles.forwardButton}>
                                    <AiOutlineForward
                                        className={styles.buttonIcon}
                                    />
                                    Forward
                                </button>
                                <button className={styles.deleteButton}>
                                    <AiOutlineDelete
                                        className={styles.buttonIcon}
                                    />
                                    Delete
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default BusinessEmail;
